<template>
    <div :style="cssVars" :key="'organisation-'+dataBelongsTo">
        <b-notification type="is-warning" role="alert" v-if="user && user.demo">
            <strong>Let op:</strong>&nbsp;Dit is een demo account.
        </b-notification>

        <div v-if="!loggedIn">
            <LoginBox
                @setLoading="setLoading"
                @setLoggedIn="setLoggedIn"
                :loading="loading"
            />
        </div>

        <div v-else>
            <FullPageLoader :isLoading="loading"/>

            <div v-if="isInvoicesOverview">
                <InvoiceExportModal
                    ref="InvoiceExportModal"
                    :passDateRange="dateRange"
                />
            </div>

            <SideNav
                :user="user"
                ref="SideNav"
            />

            <main>
                <div class="topbar"></div>

                <div class="row" v-if="environment == 'demo' || (environment == 'test' && $route.name == 'Dashboard')">
                    <div class="column">
                        <b-message title="Test/demo omgeving" aria-close-label="Sluiten" type="is-info">
                            Je bekijkt momenteel de {{ environment }} omgeving van PlanitEasy.nl, de getoonde data is fictief en bedoeld om mee te testen. E-mails welk normaal gesproken naar de klanten verstuurd worden, worden nu verstuurd naar {{ environment == 'test' ? 'info@planiteasy.nl' : 'de organisatie eigenaar' }}. Enkele functionaliteiten zijn uitgeschakeld.
                        </b-message>
                    </div>
                </div>

                <div class="row" v-if="!initialized">
                    <div class="column">
                        <b-message type="is-warning">Bezig met opstarten...</b-message>
                    </div>
                </div>

                <div class="row" v-if="showFilterBar">
                    <div class="column">
                        <div id="filterbar">
                            <div class="filterbar-left">
                                <div class="orgfilter" v-if="showOrgFilter" :key="'orgfilter-' + dataBelongsTo + Object.keys(organisations).length">
                                    <b-field label="Toon data van">
                                        <b-select v-model="dataBelongsTo" class="databelongsto">
                                            <option value="all">Alle (organisaties)</option>
                                            <option
                                                v-for="(organisation, id) in organisations"
                                                :value="id"
                                                :key="id"
                                            >({{ organisation.short_name }}) {{ organisation.name }} (#{{ organisation.id }})
                                            </option>
                                        </b-select>
                                    </b-field>
                                </div>

                                <div v-if="hasDateRangeFilter" class="daterange">
                                    <b-field :label="dateRangeFilterLabel">
                                        <b-datepicker
                                            placeholder="Klik om te selecteren"
                                            v-model="dateRange"
                                            :nearby-selectable-month-days="true"
                                            clearable
                                            range
                                        >
                                            <b-button
                                                label="Geen"
                                                type="is-danger"
                                                icon-left="times"
                                                outlined
                                                @click="dateRange = null"
                                            />
                                            <b-button
                                                label="Huidige maand"
                                                type="is-primary"
                                                icon-left="calendar-alt"
                                                outlined
                                                @click="setDateRangeThisMonth"
                                            />
                                        </b-datepicker>
                                    </b-field>
                                </div>

                                <div v-if="hasStatusFilter" class="statusfilter">
                                    <b-field label="Status">
                                        <b-dropdown
                                            aria-role="list"
                                            :triggers="['click']"
                                            v-model="isStatusDropdownActive"
                                            :can-close="['outside']"
                                        >
                                            <template #trigger>
                                                <b-button icon-right="chevron-down" :class="selectedFilters.length === 1 ? selectedFilters[0].replace('+', '') : ''">
                                                    <span v-if="selectedFilters.length === possibleFilters.length">Filter op status (alle)</span>
                                                    <span v-else-if="selectedFilters.length === 1">{{ selectedFilters[0] === 'none' ? 'Geen' : possibleFilters.find(f => f.name === selectedFilters[0]).label }}</span>
                                                    <span v-else>Filter op status: <b-tag>{{ selectedFilters.length }}</b-tag></span>
                                                </b-button>
                                            </template>

                                            <div class="statusfilter-dropdown" :key="'dropdown-content-' + selectedFilters.length + '-' + selectedFilters.join(',')">
                                                <b-dropdown-item custom class="statusfilter-toggles">
                                                    <b-button size="is-small" expanded type="is-light" @click="handleFilterClick('all')" :disabled="selectedFilters.length === possibleFilters.length">Alle</b-button>
                                                    <b-button size="is-small" expanded type="is-light" @click="handleFilterClick('none')" :disabled="selectedFilters.length === 0">Geen</b-button>
                                                </b-dropdown-item>

                                                <b-dropdown-item separator />

                                                <b-dropdown-item
                                                    aria-role="listitem"
                                                    v-for="filter in possibleFilters"
                                                    :key="filter.name"
                                                    custom
                                                    @click.stop.prevent
                                                >
                                                    <label
                                                        class="b-checkbox checkbox"
                                                        :class="filter.name.replaceAll('+', 'plusreserved')"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            :checked="selectedFilters.includes(filter.name)"
                                                            @click.stop="handleFilterClick(filter.name)"
                                                        >
                                                        <span class="check"></span>
                                                        <span class="control-label">{{ filter.label }}</span>
                                                        <span
                                                            class="status-color"
                                                            :class="filter.name.replaceAll('+', '')"
                                                        ></span>
                                                    </label>
                                                </b-dropdown-item>
                                            </div>
                                        </b-dropdown>
                                    </b-field>
                                </div>

                                <div v-if="hasInvoiceExportButton" class="exportbtn">
                                    <b-field label="Transacties"></b-field>
                                    <b-button type="is-primary" @click="$refs.InvoiceExportModal.toggleExportModal()">Exporteren</b-button>
                                </div>

                                <div v-if="hasActiveSwitch" class="activeswitch">
                                    <b-field label="Toon autorisaties"></b-field>

                                    <b-field>
                                        <b-radio-button v-model="showActive" native-value="all" type="is-primary">
                                            <span>Alles</span>
                                        </b-radio-button>
                                        <b-radio-button v-model="showActive" :native-value="true" type="is-success">
                                            <span>Actief</span>
                                        </b-radio-button>
                                        <b-radio-button v-model="showActive" :native-value="false" type="is-danger">
                                            <span>Verlopen</span>
                                        </b-radio-button>
                                    </b-field>
                                </div>
                            </div>

                            <div class="filterbar-right">
                                <div class="sensitive-data pull-right text-right">
                                    <b-field label="Gevoelige data">
                                        <b-tooltip :label="hideSensitiveData ? 'Toon gevoelige data' : 'Verberg gevoelige data'" position="is-left">
                                            <b-button :type="hideSensitiveData ? 'is-success' : 'is-danger'" :icon-right="hideSensitiveData ? 'eye' : 'eye-slash'" @click="$store.dispatch('toggleSensitiveData')"></b-button>
                                        </b-tooltip>
                                    </b-field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="user && user.temp_pass">
                    <ActivateAccount />
                </div>
                <div v-else>
                    <router-view :key="$route.path" />
                </div>

            </main>
        </div>
    </div>
</template>

<script>
import LoginBox from './LoginBox'
import SideNav from './SideNav'
import ActivateAccount from './users/ActivateAccount.vue'
import InvoiceExportModal from './invoices/components/InvoiceExportModal'
import statusFiltersMixin from '@/mixins/statusFiltersMixin'

export default {
    name: 'ThePanel',

    mixins: [statusFiltersMixin],

    components: {
        LoginBox,
        SideNav,
        ActivateAccount,
        InvoiceExportModal
    },
    data () {
        return {
            allCustomers: {},
            dateRange: null,
            statusFilter: 'all',

            loggedIn: false,
            loading: false,
            showActive: true,
            isStatusDropdownActive: false
        }
    },

    computed: {
        cssVars() {
            return {
                '--color1': this.user.organisation_color1,
                '--color2': this.user.organisation_color2
            }
        },

        initialized() {
            return this.$store.state.initialized || false
        },

        user() {
            return this.$store.state.user || {}
        },

        organisations() {
            return this.$store.state.organisations || {}
        },

        showOrgFilter(){
            return this.user && this.user?.role === 'pit' && (this.organisations && Object.keys(this.organisations).length > 1)
        },

        showFilterBar(){
            return _.every([
                !!this.filterBarPossible,
                this.user.role == 'pit' || this.hasDateRangeFilter || this.hasStatusFilter
            ])
        },

        dateRangeFilterLabel(){
            return _.has(this.$route.meta, 'dateRangeFilterLabel') ? this.$route.meta.dateRangeFilterLabel : 'Selecteer datumbereik'
        },

        hasDateRangeFilter(){
            return !!_.has(this.$route.meta, 'dateRangeFilter')
        },

        showEscalation(){
            return !!this.user.organisation_settings?.escalation == 1 || this.user.role == 'pit'
        },

        hasStatusFilter(){
            return !!_.has(this.$route.meta, 'statusFilter')
        },

        hasInvoiceExportButton(){
            return !!_.has(this.$route.meta, 'hasInvoiceExportButton') && (this.$route.path == '/panel/facturen/recent' || this.$route.path == '/panel/creditfacturen')
        },

        hasActiveSwitch(){
            return _.has(this.$route.meta, 'activeSwitch') && !!this.$route.meta.activeSwitch
        },

        filterBarPossible() {
            return _.includes([
                'AccountsOverview',
                'CommunicationOverview',
                // 'LogsOverview',
                'Dashboard',
                'MailOverview',
                'BoxesOverview',
                'ProductsOverview',
                'ContractsOverview',
                'ContractsTemplates',
                'ContractsPriceChanges',
                'InvoicesOverview',
                'CreditInvoiceOverview',
                'CustomersOverview',
                'ConnectOverview',
                'OnlineRentalSettings'
            ], this.$route.name)
        },

        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        },

        isIdle() {
            return this.$store.state.idleVue.isIdle
        },

        environment() {
            return this.$store.state.env
        },

        isInvoicesOverview() {
            return this.$route.name == 'InvoicesOverview' || this.$route.name == 'CreditInvoiceOverview'
        },

        dataBelongsTo: {
            get() {
                return this.$store.state.dataBelongsTo
            },
            set(value) {
                this.$store.dispatch('setDataBelongsTo', value)
            }
        }
    },

    methods: {
        setLoading(value) {
            this.loading = value
        },

        setLoggedIn(value){
            this.loggedIn = value

            if(value){
                document.getElementsByTagName("body")[0].classList.remove('login')
            } else {
                document.getElementsByTagName("body")[0].classList.add('login')
            }

            if (!!this.loggedIn) {
                this.checkSensitiveData()
            }

            setTimeout(() => {
                this.$store.dispatch('setAuthRedirected', true)
            }, 3000)
        },

        setDateRangeThisMonth(){
            const thisMonth = [moment(new Date()).startOf('month')._d, moment(new Date()).endOf('month')._d]
            this.$store.dispatch('setDateRange', thisMonth)
            this.dateRange = thisMonth
        },

        checkSensitiveData() {
            if(this.user && _.has(this.user, 'settings.sensitivedata') && this.user.settings.sensitivedata === 0) {
                this.$store.dispatch('toggleSensitiveData', false)
            }
        }
    },

    watch: {
        '$store.state.dataBelongsTo': function(newVal, oldVal){
            if(newVal !== oldVal){
                this.dataBelongsTo = newVal
            }
        },

        dateRange(newVal){
            this.$store.dispatch('setDateRange', newVal)
        },

        showActive(newVal) {
            this.$store.dispatch('setShowActive', newVal)
        },

        isIdle(newVal){
            if(newVal === true) {
                if(this.loggedIn){
                    window.location = '/logout/force'
                }
            }
        },

        showEscalation(newVal){
            if(newVal === true){
                this.addEscalationFilters()
            } else {
                this.possibleFilters = this.possibleFilters.filter(filter => filter.name !== 'escalated' && filter.name !== 'escalated_deurwaarder')
            }
        }
    }
}
</script>

<style lang="scss">
    $color1: var(--color1);
    $color2: var(--color2);

    #panel main .topbar {
        background-color: $color1;
    }
</style>